import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import {
  readOfflineSession,
  saveOfflineSession,
} from "@/features/offline/offline-db";
import { api } from "@/lib/api";
import type { ReactNode } from "react";

export async function sessionWithOfflineFallback() {
  try {
    const session = await api.session();
    await saveOfflineSession(session);
    return session;
  } catch (error) {
    if (
      typeof navigator !== "undefined" &&
      (!navigator.onLine || error instanceof TypeError)
    ) {
      const cached = await readOfflineSession();
      if (cached) return cached;
    }
    throw error;
  }
}

export function RequireAuth() {
  const location = useLocation();
  const session = useQuery({
    queryKey: ["auth-session"],
    queryFn: sessionWithOfflineFallback,
    staleTime: 60_000,
    retry: false,
  });
  if (session.isLoading)
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <div className="w-72 space-y-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  if (!session.data?.authenticated)
    return (
      <Navigate to="/acceso" replace state={{ from: location.pathname }} />
    );
  if (
    session.data.user?.mustChangePassword &&
    location.pathname !== "/cambiar-contrasena"
  )
    return <Navigate to="/cambiar-contrasena" replace />;
  if (
    !session.data.user?.mustChangePassword &&
    location.pathname === "/cambiar-contrasena"
  )
    return <Navigate to="/mapa" replace />;
  return <Outlet />;
}

export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: string[];
  children: ReactNode;
}) {
  const session = useQuery({
    queryKey: ["auth-session"],
    queryFn: sessionWithOfflineFallback,
    staleTime: 60_000,
    retry: false,
  });
  const permissions = session.data?.user?.permissions ?? [];
  if (!anyOf.some((permission) => permissions.includes(permission)))
    return <Navigate to="/mapa" replace />;
  return children;
}
